import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Button, Input, InputNumber, Popover, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { modTypes } from "@blings/blings-player";
import { motionAPI } from "@blings/blings-player/editor-tools";
import { useMst } from "../../stores/main";
import { ColorPicker } from "../../BaseComps/ColorPicker";
import { HoverTooltip } from "../../BaseComps/InfoTooltip";
import { frameToPixel } from "./utils/timelineHelpers";
import {
  getKeyframePropertyPath,
  getLayerKeyframeInfo,
  normalizeKeyframePath,
  TREE_ROW_HEIGHT,
} from "./utils/keyframeHelpers";
import type { KeyframeInfo } from "./utils/keyframeHelpers";
import { useLayerEdgeDrag } from "./hooks/useLayerEdgeDrag";
import { useLayerBodyDrag, SelectedLayerInfo } from "./hooks/useLayerBodyDrag";
import { useAnimationJsonEdit } from "./hooks/useAnimationJsonEdit";
import {
  getKeyframeDragGroupKey,
  getMarkerElements,
  orderKeyframeDragItemsForApply,
  parseMarkerDragItem,
  useKeyframeDrag,
} from "./hooks/useKeyframeDrag";
import type { CapturedKeyframeDragItem } from "./hooks/useKeyframeDrag";
import {
  clampKeyframeDragDelta,
  findKeyframeTimeConstraints,
  getSelectedKeyframeDragBounds,
} from "./hooks/dragUtils";
import {
  isReadableMod,
  modTargetsLayer,
  normalizeAssetId,
} from "./utils/timingModHelpers";
import { getLayerData } from "./utils/layerHierarchy";
import type {
  TimelineTreeNode,
  TimelineLayerNode,
  TimelinePropertyNode,
  TimelineLayerInfo,
} from "./utils/layerHierarchy";
import type { IModModel } from "../../stores/ModsStore";
import type {
  LayerDragPreview,
  LayerDragPreviewLayer,
  LayerEdgeDragPreview,
} from "../../stores/TimelineStore";
import { applyCustomModsToLayerData } from "./utils/moddedLayerData";
import { getMotionPropertyForRow } from "./utils/propertyAnimationModHelpers";
import { KeyframeEasingEditor } from "./KeyframeEasingEditor";
import {
  detectTimelineEasingPreset,
  getTimelineBezierValue,
  getTimelineEasingDisplayName,
  TIMELINE_EASING_PRESETS,
} from "./utils/keyframeSegmentHelpers";
import { doesKeyframeSegmentChangeValue } from "./utils/keyframeSegmentVisibility";
import type {
  TimelineBezierValue,
  TimelineEasingPreset,
} from "./utils/keyframeSegmentHelpers";
import {
  buildTimelineKeyframeDataAttributes,
  buildTimelineKeyframeId,
} from "./utils/keyframeSelectionHelpers";
import {
  getValueAtObjectPath,
  removeKeyframeFromAnimatedPropertyValue,
} from "./utils/rootAnimationModHelpers";
import { updateObjectValues } from "../../helpers/JsonSearch";

/** Upper bound for viewport-culling checks (px). Set generously to cover ultra-wide monitors. */
const MAX_VISIBLE_VIEWPORT_WIDTH = 4096;

interface TimelineTreeNodeTrackProps {
  node: TimelineTreeNode;
  compositionStart: number;
  compositionEnd: number;
  totalFrames: number;
  isSelected: boolean;
  isHovered: boolean;
  isRowHovered: boolean;
  onClick: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  selectedLayersWithTiming: SelectedLayerInfo[];
  selectedKeyframeIds: Set<string>;
  onKeyframeClick: (keyframeId: string, event: React.MouseEvent) => void;
  clipToWorkarea: boolean;
}

type EdgeType = "left" | "right" | null;
type EditableKeyframeDraft = Array<number | null>;
type KeyframeEditorMode = "numeric" | "color" | "json";
type KeyframeEditorState = {
  path: string;
  mode: KeyframeEditorMode;
  linkScaleXY: boolean;
  draftValues?: EditableKeyframeDraft;
  draftText?: string;
};

type KeyframeSegment = {
  id: string;
  startKeyframeId: string;
  endKeyframeId: string;
  propertyPath: string;
  startKeyframe: KeyframeInfo;
  endKeyframe: KeyframeInfo;
  startTime: number;
  endTime: number;
  startPixel: number;
  endPixel: number;
  displayName: string;
  easingPreset: TimelineEasingPreset;
  bezier: TimelineBezierValue;
};

type SegmentDragState = {
  segment: KeyframeSegment;
  items: CapturedKeyframeDragItem[];
  keyframeIds: string[];
  startClientX: number;
  previewDelta: number;
  minDelta: number;
  maxDelta: number;
  hasMoved: boolean;
};

type EasingEditorState = {
  segment: KeyframeSegment;
  preset: TimelineEasingPreset;
  bezier: TimelineBezierValue;
};

type AddKeyframeField = {
  id: string;
  label: string;
  path: string;
  componentIndex: number;
};

type AddKeyframeEditorState = {
  localFrame: number;
  fields: AddKeyframeField[];
  draftValues: EditableKeyframeDraft;
  propertyPaths: string[];
  linkScaleXY: boolean;
  mode: Exclude<KeyframeEditorMode, "json">;
};

type ResolvedPropertyKeyframe = {
  keyframe: KeyframeInfo;
  resolvedTime: number;
  resolvedValue: number[] | null;
};

type KeyframeTimelinePosition = {
  pixel: number;
  resolvedTime: number;
  compositionTime: number;
  resolvedValue: unknown;
  resolvedOutgoingValue: unknown;
};

const KEYFRAME_VALUE_SUFFIX_REGEX = /\.t$/;
const KEYFRAME_DRAG_THRESHOLD_PX = 4;
const DEFAULT_COLOR_HEX = "#ffffff";

function getKeyframeValuePath(path: string): string {
  return normalizeKeyframePath(path).replace(KEYFRAME_VALUE_SUFFIX_REGEX, ".s");
}

function getKeyframeOutgoingValuePath(path: string): string {
  return normalizeKeyframePath(path).replace(KEYFRAME_VALUE_SUFFIX_REGEX, ".e");
}

function formatNumericValue(value: number): string {
  return Number.isInteger(value)
    ? value.toString()
    : value.toFixed(2).replace(/\.?0+$/, "");
}

function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

function cloneStructuredValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function interpolateNumericValue(
  startValue: number,
  endValue: number,
  progress: number
): number {
  return startValue + (endValue - startValue) * progress;
}

function normalizeEditableKeyframeValue(value: unknown): number[] | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return [value];
  }

  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => typeof entry === "number" && Number.isFinite(entry))
  ) {
    return value;
  }

  return null;
}

function toTimelineEasingKeyframe(keyframe: KeyframeInfo["originalKeyframe"]) {
  const normalizeArrayValue = (value: unknown): number[] | undefined => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return [value];
    }

    if (
      Array.isArray(value) &&
      value.every(
        (entry) => typeof entry === "number" && Number.isFinite(entry)
      )
    ) {
      return value;
    }

    return undefined;
  };

  return {
    t: keyframe.t,
    h: keyframe.h,
    i: keyframe.i,
    o: keyframe.o,
    s: normalizeArrayValue(keyframe.s),
    e: normalizeArrayValue(keyframe.e),
  };
}

function isColorPropertyPath(path: string): boolean {
  return /\.(fc|sc|c)$/.test(normalizeKeyframePath(path));
}

function isColorKeyframeValue(keyframe: KeyframeInfo, value: unknown): boolean {
  const editableValue = normalizeEditableKeyframeValue(value);
  if (!editableValue || editableValue.length < 3) {
    return false;
  }

  return (
    isColorPropertyPath(getKeyframePropertyPath(keyframe.path)) ||
    /color/i.test(keyframe.propertyName)
  );
}

function toColorHex(value: number[] | null | undefined): string {
  if (!value || value.length < 3) {
    return DEFAULT_COLOR_HEX;
  }

  const toByte = (component: number) =>
    Math.max(
      0,
      Math.min(
        255,
        Math.round((component <= 1 ? component : component / 255) * 255)
      )
    );

  return `#${value
    .slice(0, 3)
    .map((component) => toByte(component).toString(16).padStart(2, "0"))
    .join("")}`;
}

function fromColorHex(
  hex: string,
  previousValue: EditableKeyframeDraft
): EditableKeyframeDraft {
  const normalizedHex = hex.replace("#", "");
  if (normalizedHex.length !== 6) {
    return previousValue;
  }

  const nextDraftValues = previousValue.slice();
  nextDraftValues[0] = parseInt(normalizedHex.slice(0, 2), 16) / 255;
  nextDraftValues[1] = parseInt(normalizedHex.slice(2, 4), 16) / 255;
  nextDraftValues[2] = parseInt(normalizedHex.slice(4, 6), 16) / 255;
  return nextDraftValues;
}

function getKeyframeEditorMode(
  keyframe: KeyframeInfo,
  value: unknown
): KeyframeEditorMode {
  if (isColorKeyframeValue(keyframe, value)) {
    return "color";
  }

  if (normalizeEditableKeyframeValue(value)) {
    return "numeric";
  }

  return "json";
}

function normalizeDraftValues(
  draftValues: EditableKeyframeDraft
): number[] | null {
  if (
    draftValues.some(
      (entry) => typeof entry !== "number" || !Number.isFinite(entry)
    )
  ) {
    return null;
  }

  return draftValues as number[];
}

function getMotionEasingPatch(
  preset: TimelineEasingPreset,
  bezier: TimelineBezierValue
) {
  if (preset === "hold") {
    return { hold: true };
  }

  if (preset !== "custom") {
    return {
      easing: preset as Exclude<TimelineEasingPreset, "custom" | "hold">,
    };
  }

  return {
    easing: {
      out: {
        x: bezier.x1,
        y: bezier.y1,
      },
      in: {
        x: bezier.x2,
        y: bezier.y2,
      },
    },
  };
}

function getTimelineMotionTarget(
  layer: TimelineLayerInfo
): {
  assetId: string | undefined;
  layerName: string;
  layerUid: number | undefined;
} {
  return {
    assetId: normalizeAssetId(layer.assetId),
    layerName: layer.nm,
    layerUid: layer.uid,
  };
}

function getResolvedPropertyKeyframes(
  group: KeyframeInfo[],
  keyframePositionByPath: Map<string, KeyframeTimelinePosition>
): ResolvedPropertyKeyframe[] {
  return group
    .map((keyframe) => {
      const entry = keyframePositionByPath.get(
        normalizeKeyframePath(keyframe.path)
      );
      return {
        keyframe,
        resolvedTime: entry?.resolvedTime ?? keyframe.t,
        resolvedValue:
          normalizeEditableKeyframeValue(
            entry?.resolvedValue ?? keyframe.originalKeyframe.s
          ) ?? null,
      };
    })
    .sort((left, right) => left.resolvedTime - right.resolvedTime);
}

function getAddKeyframeDraftSource(params: {
  group: KeyframeInfo[];
  keyframePositionByPath: Map<string, KeyframeTimelinePosition>;
  currentLocalFrame: number;
  modsByNormalizedPath: Map<string, IModModel>;
}): {
  hasExactKeyframeAtFrame: boolean;
  labelSourceKeyframe: KeyframeInfo | null;
  sourceValue: number[] | null;
} {
  const {
    group,
    keyframePositionByPath,
    currentLocalFrame,
    modsByNormalizedPath,
  } = params;
  const resolvedGroup = getResolvedPropertyKeyframes(
    group,
    keyframePositionByPath
  );

  if (resolvedGroup.some((entry) => entry.resolvedTime === currentLocalFrame)) {
    return {
      hasExactKeyframeAtFrame: true,
      labelSourceKeyframe: null,
      sourceValue: null,
    };
  }

  const previousEntry = [...resolvedGroup]
    .reverse()
    .find((entry) => entry.resolvedTime < currentLocalFrame);
  const nextEntry = resolvedGroup.find(
    (entry) => entry.resolvedTime > currentLocalFrame
  );
  const previousValue = previousEntry?.resolvedValue;
  const nextValue = nextEntry?.resolvedValue;
  const outgoingValueMod = previousEntry
    ? modsByNormalizedPath.get(
        getKeyframeOutgoingValuePath(previousEntry.keyframe.path)
      )
    : undefined;
  const previousOutgoingValue = previousEntry
    ? normalizeEditableKeyframeValue(
        (outgoingValueMod && isReadableMod(outgoingValueMod)
          ? outgoingValueMod.moddata.value
          : undefined) ??
          previousEntry.keyframe.originalKeyframe.e ??
          previousValue
      )
    : null;

  if (previousEntry && nextEntry && previousOutgoingValue && nextValue) {
    const duration = nextEntry.resolvedTime - previousEntry.resolvedTime;
    const progress =
      duration <= 0
        ? 0
        : (currentLocalFrame - previousEntry.resolvedTime) / duration;
    const clampedProgress = Math.max(0, Math.min(1, progress));

    return {
      hasExactKeyframeAtFrame: false,
      labelSourceKeyframe: previousEntry.keyframe,
      sourceValue:
        previousEntry.keyframe.originalKeyframe.h === 1
          ? previousValue ?? previousOutgoingValue
          : previousOutgoingValue.map((startValue, componentIndex) =>
              interpolateNumericValue(
                startValue,
                nextValue[componentIndex] ?? startValue,
                clampedProgress
              )
            ),
    };
  }

  return {
    hasExactKeyframeAtFrame: false,
    labelSourceKeyframe:
      previousEntry?.keyframe ??
      nextEntry?.keyframe ??
      resolvedGroup[0]?.keyframe ??
      null,
    sourceValue: previousValue ?? nextValue ?? null,
  };
}

type KeyframeFieldDescriptor = {
  label: string;
  componentIndex: number;
};

function getKeyframeFieldDescriptors(
  keyframe: KeyframeInfo,
  valueCount: number
): KeyframeFieldDescriptor[] {
  if (valueCount <= 1) {
    if (keyframe.splitDimension === "x")
      return [{ label: "X", componentIndex: 0 }];
    if (keyframe.splitDimension === "y")
      return [{ label: "Y", componentIndex: 0 }];
    if (keyframe.propertyType === "rotation") {
      return [{ label: "Rotation", componentIndex: 0 }];
    }
    if (keyframe.propertyType === "opacity") {
      return [{ label: "Opacity", componentIndex: 0 }];
    }
    if (keyframe.propertyType === "skew") {
      return [{ label: "Skew", componentIndex: 0 }];
    }
    if (keyframe.propertyType === "skewAxis") {
      return [{ label: "Axis", componentIndex: 0 }];
    }
    return [{ label: "Value", componentIndex: 0 }];
  }

  if (
    keyframe.propertyType === "position" ||
    keyframe.propertyType === "scale"
  ) {
    return ["X", "Y"]
      .slice(0, Math.min(valueCount, 2))
      .map((label, componentIndex) => ({ label, componentIndex }));
  }

  if (keyframe.propertyType === "anchor") {
    return ["X", "Y", "Z"]
      .slice(0, valueCount)
      .map((label, componentIndex) => ({ label, componentIndex }));
  }

  return Array.from({ length: valueCount }, (_, index) => ({
    label: `${index + 1}`,
    componentIndex: index,
  }));
}

function isScaleXYLinkable(
  keyframe: KeyframeInfo,
  valueCount: number
): boolean {
  return keyframe.propertyType === "scale" && valueCount >= 2;
}

function syncLinkedScaleDraftValues(
  draftValues: EditableKeyframeDraft,
  sourceIndex = 0,
  basisValues: EditableKeyframeDraft = draftValues
): EditableKeyframeDraft {
  if (draftValues.length < 2) return draftValues;
  const nextDraftValues = draftValues.slice();
  const otherIndex = sourceIndex === 0 ? 1 : 0;
  const sourceValue = nextDraftValues[sourceIndex] ?? null;
  const basisSourceValue = basisValues[sourceIndex] ?? null;
  const basisOtherValue = basisValues[otherIndex] ?? null;

  if (sourceValue === null) {
    nextDraftValues[otherIndex] = null;
    return nextDraftValues;
  }

  if (basisSourceValue === null || basisOtherValue === null) {
    nextDraftValues[otherIndex] = sourceValue;
    return nextDraftValues;
  }

  if (basisSourceValue === 0) {
    if (basisOtherValue === 0) {
      nextDraftValues[otherIndex] = sourceValue;
    }
    return nextDraftValues;
  }

  nextDraftValues[otherIndex] = roundToTwoDecimals(
    (basisOtherValue / basisSourceValue) * sourceValue
  );
  return nextDraftValues;
}

function formatKeyframeValueLabel(
  keyframe: KeyframeInfo,
  value: unknown
): string {
  const editableValue = normalizeEditableKeyframeValue(value);
  if (isColorKeyframeValue(keyframe, value)) {
    const alphaValue =
      editableValue && editableValue.length > 3
        ? ` • A: ${formatNumericValue(editableValue[3])}`
        : "";
    return `${toColorHex(editableValue)}${alphaValue}`;
  }

  if (!editableValue) {
    try {
      return JSON.stringify(value);
    } catch {
      return "Unsupported value";
    }
  }

  const fields = getKeyframeFieldDescriptors(keyframe, editableValue.length);
  return fields
    .map(
      ({ label, componentIndex }) =>
        `${label}: ${formatNumericValue(editableValue[componentIndex])}`
    )
    .join(" • ");
}

/**
 * Get resolved layer timing values by checking Custom Change mods first,
 * then falling back to original layer values
 */
export function getResolvedLayerTiming(
  layer: TimelineLayerInfo,
  mods: IModModel[]
): { ip: number; op: number; st: number; timelineOffset: number } {
  let ip = layer.ip;
  let op = layer.op;
  let st = layer.st ?? 0;
  const timelineOffset = layer.timelineOffset ?? 0;

  // Find Custom Change mods that target this layer's timing properties
  for (const mod of mods) {
    if (!isReadableMod(mod)) continue;

    const { moddata } = mod;
    if (moddata.type !== modTypes.custom) continue;
    if (moddata.isDisabled) continue;
    if (!modTargetsLayer(mod, layer)) continue;

    const path = moddata.path;
    const value = moddata.value;

    if (typeof value === "number") {
      if (path === "ip") ip = value + timelineOffset;
      else if (path === "op") op = value + timelineOffset;
      else if (path === "st") st = value + timelineOffset;
    }
  }

  return { ip, op, st, timelineOffset };
}

function previewLayerMatchesTarget(
  previewLayer: LayerDragPreviewLayer,
  layer: TimelineLayerInfo
): boolean {
  if (previewLayer.instancePath) {
    return previewLayer.instancePath === layer.instancePath;
  }

  if (
    layer.uid !== undefined &&
    previewLayer.layerUid === layer.uid &&
    previewLayer.assetId === normalizeAssetId(layer.assetId)
  ) {
    return true;
  }

  return (
    previewLayer.layerName === layer.nm &&
    previewLayer.assetId === normalizeAssetId(layer.assetId)
  );
}

function previewLayersIncludeLayer(
  previewLayers: LayerDragPreviewLayer[],
  layer: TimelineLayerInfo
): boolean {
  return previewLayers.some((previewLayer) => {
    if (previewLayerMatchesTarget(previewLayer, layer)) {
      return true;
    }

    return layer.instancePath.startsWith(`${previewLayer.instancePath}::`);
  });
}

function getPreviewDragDeltaForLayer(
  layerDragPreview: LayerDragPreview | null,
  layer: TimelineLayerInfo
): number {
  if (!layerDragPreview?.layers.length) {
    return 0;
  }

  const isAffected = previewLayersIncludeLayer(layerDragPreview.layers, layer);

  return isAffected ? layerDragPreview.delta : 0;
}

function isLayerAffectedByPreview(
  layerDragPreview: LayerDragPreview | null,
  layer: TimelineLayerInfo
): boolean {
  if (!layerDragPreview?.layers.length) {
    return false;
  }

  return previewLayersIncludeLayer(layerDragPreview.layers, layer);
}

function getPreviewEdgeDeltaForLayer(
  layerEdgeDragPreview: LayerEdgeDragPreview | null,
  layer: TimelineLayerInfo,
  edge: "left" | "right"
): number {
  if (
    !layerEdgeDragPreview?.layers.length ||
    layerEdgeDragPreview.edge !== edge
  ) {
    return 0;
  }

  const isAffected = previewLayersIncludeLayer(
    layerEdgeDragPreview.layers,
    layer
  );

  return isAffected ? layerEdgeDragPreview.delta : 0;
}

function isLayerAffectedByEdgePreview(
  layerEdgeDragPreview: LayerEdgeDragPreview | null,
  layer: TimelineLayerInfo
): boolean {
  if (!layerEdgeDragPreview?.layers.length) {
    return false;
  }

  return previewLayersIncludeLayer(layerEdgeDragPreview.layers, layer);
}

// Layer track component - renders the duration bar
const LayerTrack = observer(
  ({
    node,
    compositionStart,
    compositionEnd,
    totalFrames,
    isSelected,
    isHovered,
    isRowHovered,
    onClick,
    onContextMenu,
    onMouseEnter,
    onMouseLeave,
    selectedLayersWithTiming,
    clipToWorkarea,
  }: {
    node: TimelineLayerNode;
    compositionStart: number;
    compositionEnd: number;
    totalFrames: number;
    isSelected: boolean;
    isHovered: boolean;
    isRowHovered: boolean;
    onClick: (e: React.MouseEvent) => void;
    onContextMenu: (e: React.MouseEvent) => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    selectedLayersWithTiming: SelectedLayerInfo[];
    clipToWorkarea: boolean;
  }) => {
    const {
      timelineStore: {
        zoom,
        scrollOffset,
        viewportWidth,
        layerDragPreview,
        layerEdgeDragPreview,
      },
      modsStore,
    } = useMst();
    const visibleViewportWidth =
      viewportWidth > 0 ? viewportWidth : MAX_VISIBLE_VIEWPORT_WIDTH;

    const { layer } = node;
    const [hoveredEdge, setHoveredEdge] = useState<EdgeType>(null);
    const [isBarHovered, setIsBarHovered] = useState(false);

    // Get resolved layer timing (includes Custom Change mod values)
    const resolvedTiming = getResolvedLayerTiming(
      layer,
      modsStore.mods?.slice() ?? []
    );

    // Get edge drag functionality - pass resolved timing for correct initial values
    const { isDragging, dragEdge, previewFrame, startDrag } = useLayerEdgeDrag(
      layer,
      compositionStart,
      resolvedTiming,
      selectedLayersWithTiming,
      isSelected
    );

    // Get body drag functionality for dragging entire layer
    const {
      isDragging: isBodyDragging,
      previewIp,
      previewOp,
      startDrag: startBodyDrag,
      justDragged,
    } = useLayerBodyDrag(
      layer,
      compositionStart,
      resolvedTiming,
      selectedLayersWithTiming,
      isSelected
    );

    // Wrap onClick to skip selection changes if we just finished a drag
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          return;
        }

        if (justDragged) return;
        onClick(e);
      },
      [justDragged, onClick]
    );

    const handleBarMouseDown = useCallback(
      (e: React.MouseEvent) => {
        // Let modifier-assisted clicks behave like row selection instead of
        // starting a drag gesture first.
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          onClick(e);
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        startBodyDrag(e);
      },
      [onClick, startBodyDrag]
    );

    // Check if this layer is part of a multi-drag (but not the initiator)
    const previewDragDelta = useMemo(
      () =>
        isBodyDragging
          ? 0
          : getPreviewDragDeltaForLayer(layerDragPreview, layer),
      [isBodyDragging, layer, layerDragPreview]
    );
    const isPreviewAffected = useMemo(
      () =>
        !isBodyDragging
          ? isLayerAffectedByPreview(layerDragPreview, layer)
          : false,
      [isBodyDragging, layer, layerDragPreview]
    );

    // Get the drag delta for multi-drag preview
    const isPartOfMultiDrag = isPreviewAffected;
    const multiDragDelta = previewDragDelta;
    const edgePreviewLeftDelta = useMemo(
      () =>
        isDragging
          ? 0
          : getPreviewEdgeDeltaForLayer(layerEdgeDragPreview, layer, "left"),
      [isDragging, layer, layerEdgeDragPreview]
    );
    const edgePreviewRightDelta = useMemo(
      () =>
        isDragging
          ? 0
          : getPreviewEdgeDeltaForLayer(layerEdgeDragPreview, layer, "right"),
      [isDragging, layer, layerEdgeDragPreview]
    );
    const isPartOfMultiEdgeDrag = useMemo(
      () =>
        !isDragging &&
        isLayerAffectedByEdgePreview(layerEdgeDragPreview, layer),
      [isDragging, layer, layerEdgeDragPreview]
    );

    // Edge handle mouse handlers - must be defined before early return
    const handleEdgeMouseDown = useCallback(
      (e: React.MouseEvent, edge: "left" | "right") => {
        // Let modifier-assisted clicks behave like selection gestures even
        // when they land on the resize handles.
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          onClick(e);
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        startDrag(e, edge);
      },
      [onClick, startDrag]
    );

    const handleEdgeMouseEnter = useCallback((edge: "left" | "right") => {
      setHoveredEdge(edge);
    }, []);

    const handleEdgeMouseLeave = useCallback(() => {
      setHoveredEdge(null);
    }, []);

    // Render edge handles for a bar segment
    const renderEdgeHandles = useCallback(
      (isLeftEdge: boolean, isRightEdge: boolean) => {
        return (
          <>
            {isLeftEdge && (
              <div
                className={`timeline-track-bar__edge-handle timeline-track-bar__edge-handle--left ${
                  hoveredEdge === "left"
                    ? "timeline-track-bar__edge-handle--hovered"
                    : ""
                }`}
                onMouseDown={(e) => handleEdgeMouseDown(e, "left")}
                onMouseEnter={() => handleEdgeMouseEnter("left")}
                onMouseLeave={handleEdgeMouseLeave}
              />
            )}
            {isRightEdge && (
              <div
                className={`timeline-track-bar__edge-handle timeline-track-bar__edge-handle--right ${
                  hoveredEdge === "right"
                    ? "timeline-track-bar__edge-handle--hovered"
                    : ""
                }`}
                onMouseDown={(e) => handleEdgeMouseDown(e, "right")}
                onMouseEnter={() => handleEdgeMouseEnter("right")}
                onMouseLeave={handleEdgeMouseLeave}
              />
            )}
          </>
        );
      },
      [
        hoveredEdge,
        handleEdgeMouseDown,
        handleEdgeMouseEnter,
        handleEdgeMouseLeave,
      ]
    );

    // Use preview values during drag, otherwise use resolved values from mods
    // Body drag takes priority, then multi-drag, then edge drag, then resolved timing
    const effectiveStart =
      isBodyDragging && previewIp !== null
        ? previewIp
        : isPartOfMultiDrag
        ? resolvedTiming.ip + multiDragDelta
        : edgePreviewLeftDelta !== 0
        ? resolvedTiming.ip + edgePreviewLeftDelta
        : isDragging && dragEdge === "left" && previewFrame !== null
        ? previewFrame
        : resolvedTiming.ip;
    const effectiveEnd =
      isBodyDragging && previewOp !== null
        ? previewOp
        : isPartOfMultiDrag
        ? resolvedTiming.op + multiDragDelta
        : edgePreviewRightDelta !== 0
        ? resolvedTiming.op + edgePreviewRightDelta
        : isDragging && dragEdge === "right" && previewFrame !== null
        ? previewFrame
        : resolvedTiming.op;

    const start = effectiveStart;
    const end = effectiveEnd;

    // Use compositionEnd prop which already accounts for trim/extend state
    const sceneEnd = compositionEnd;

    // Detect out-of-bounds regions (only show overflow when not clipping to workarea)
    const hasLeftOverflow = !clipToWorkarea && start < compositionStart;
    const hasRightOverflow = !clipToWorkarea && end > sceneEnd;

    // Calculate segment bounds
    const inBoundsStart = Math.max(start, compositionStart);
    const inBoundsEnd = Math.min(end, sceneEnd);

    // Calculate positions relative to composition start
    const relativeStart = start - compositionStart;
    const relativeEnd = end - compositionStart;

    const left = frameToPixel(relativeStart, zoom, scrollOffset);
    const right = frameToPixel(relativeEnd, zoom, scrollOffset);

    // Don't render bar if completely off screen
    if (right < 0 || left > visibleViewportWidth) {
      return (
        <div
          className={`timeline-tree-track timeline-tree-track--layer ${
            isSelected ? "timeline-tree-track--selected" : ""
          } ${isHovered ? "timeline-tree-track--hovered" : ""} ${
            isRowHovered ? "timeline-tree-track--row-hovered" : ""
          }`}
          style={{ height: TREE_ROW_HEIGHT }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      );
    }

    const trackClassNames = [
      "timeline-tree-track",
      "timeline-tree-track--layer",
      isSelected && "timeline-tree-track--selected",
      isHovered && "timeline-tree-track--hovered",
      isRowHovered && "timeline-tree-track--row-hovered",
      isBarHovered && "timeline-tree-track--bar-hovered",
    ]
      .filter(Boolean)
      .join(" ");

    const barClassNames = [
      "timeline-track-bar",
      typeof layer.ty === "number" && `timeline-track-bar--type-${layer.ty}`,
      isSelected && "selected",
      (isDragging ||
        isBodyDragging ||
        isPartOfMultiDrag ||
        isPartOfMultiEdgeDrag) &&
        "timeline-track-bar--dragging",
    ]
      .filter(Boolean)
      .join(" ");

    const outOfBoundsClassNames = [
      ...barClassNames.split(" "),
      "timeline-track-bar--out-of-bounds",
    ].join(" ");

    // Calculate pixel positions for segments
    const leftOverflowLeft = frameToPixel(relativeStart, zoom, scrollOffset);
    const leftOverflowRight = frameToPixel(0, zoom, scrollOffset);
    const inBoundsLeft = frameToPixel(
      inBoundsStart - compositionStart,
      zoom,
      scrollOffset
    );
    const inBoundsRight = frameToPixel(
      inBoundsEnd - compositionStart,
      zoom,
      scrollOffset
    );
    const rightOverflowLeft = frameToPixel(
      sceneEnd - compositionStart,
      zoom,
      scrollOffset
    );
    const rightOverflowRight = frameToPixel(relativeEnd, zoom, scrollOffset);

    // Calculate widths
    const inBoundsWidth =
      inBoundsEnd > inBoundsStart
        ? Math.max(2, inBoundsRight - inBoundsLeft)
        : 0;
    const isLeftOverflowEdgeVisible = leftOverflowLeft >= 0;
    const isInBoundsLeftEdgeVisible = !hasLeftOverflow && inBoundsLeft >= 0;
    const isInBoundsRightEdgeVisible =
      !hasRightOverflow && inBoundsRight <= visibleViewportWidth;
    const isRightOverflowEdgeVisible =
      rightOverflowRight <= visibleViewportWidth;

    return (
      <div
        className={trackClassNames}
        style={{ height: TREE_ROW_HEIGHT }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Left overflow segment */}
        {hasLeftOverflow && leftOverflowRight > 0 && (
          <div
            className={outOfBoundsClassNames}
            data-node-key={node.nodeKey}
            style={{
              left: Math.max(0, leftOverflowLeft),
              width: Math.max(
                0,
                Math.min(leftOverflowRight, visibleViewportWidth) -
                  Math.max(0, leftOverflowLeft)
              ),
              borderRadius: isLeftOverflowEdgeVisible
                ? "var(--radius) 0 0 var(--radius)"
                : "0",
            }}
            onMouseDown={handleBarMouseDown}
            onMouseEnter={() => setIsBarHovered(true)}
            onMouseLeave={() => setIsBarHovered(false)}
            onClick={handleClick}
            onContextMenu={onContextMenu}
            title={`${layer.nm} (${Math.round(start)}-${Math.round(end)})`}
          >
            {renderEdgeHandles(isLeftOverflowEdgeVisible, false)}
          </div>
        )}

        {/* In-bounds segment */}
        {inBoundsWidth > 0 &&
          inBoundsRight > 0 &&
          inBoundsLeft < visibleViewportWidth && (
            <div
              className={barClassNames}
              data-node-key={node.nodeKey}
              style={{
                left: Math.max(0, inBoundsLeft),
                width: Math.max(
                  0,
                  Math.min(inBoundsRight, visibleViewportWidth) -
                    Math.max(0, inBoundsLeft)
                ),
                borderRadius: `${
                  isInBoundsLeftEdgeVisible ? "var(--radius)" : "0"
                } ${isInBoundsRightEdgeVisible ? "var(--radius)" : "0"} ${
                  isInBoundsRightEdgeVisible ? "var(--radius)" : "0"
                } ${isInBoundsLeftEdgeVisible ? "var(--radius)" : "0"}`,
              }}
              onMouseDown={handleBarMouseDown}
              onMouseEnter={() => setIsBarHovered(true)}
              onMouseLeave={() => setIsBarHovered(false)}
              onClick={handleClick}
              onContextMenu={onContextMenu}
              title={`${layer.nm} (${Math.round(start)}-${Math.round(end)})`}
            >
              {/* Chevron indicators - show on hover */}
              {isInBoundsLeftEdgeVisible && (
                <span className="timeline-track-bar__chevron timeline-track-bar__chevron--left">
                  ‹
                </span>
              )}
              {isInBoundsRightEdgeVisible && (
                <span className="timeline-track-bar__chevron timeline-track-bar__chevron--right">
                  ›
                </span>
              )}
              {renderEdgeHandles(
                isInBoundsLeftEdgeVisible,
                isInBoundsRightEdgeVisible
              )}
            </div>
          )}

        {/* Right overflow segment */}
        {hasRightOverflow && rightOverflowLeft < visibleViewportWidth && (
          <div
            className={outOfBoundsClassNames}
            data-node-key={node.nodeKey}
            style={{
              left: Math.max(0, rightOverflowLeft),
              width: Math.max(
                0,
                Math.min(rightOverflowRight, visibleViewportWidth) -
                  Math.max(0, rightOverflowLeft)
              ),
              borderRadius: isRightOverflowEdgeVisible
                ? "0 var(--radius) var(--radius) 0"
                : "0",
            }}
            onMouseDown={handleBarMouseDown}
            onMouseEnter={() => setIsBarHovered(true)}
            onMouseLeave={() => setIsBarHovered(false)}
            onClick={handleClick}
            onContextMenu={onContextMenu}
            title={`${layer.nm} (${Math.round(start)}-${Math.round(end)})`}
          >
            {renderEdgeHandles(false, isRightOverflowEdgeVisible)}
          </div>
        )}
      </div>
    );
  }
);

// Property track component - renders keyframe markers
const PropertyTrack = observer(
  ({
    node,
    compositionStart,
    selectedKeyframeIds,
    onKeyframeClick,
  }: {
    node: TimelinePropertyNode;
    compositionStart: number;
    selectedKeyframeIds: Set<string>;
    onKeyframeClick: (keyframeId: string, event: React.MouseEvent) => void;
  }) => {
    const {
      timelineStore,
      playerStore: { jsonFile, currentFrame },
      modsStore,
      undoStore,
    } = useMst();
    const {
      zoom,
      scrollOffset,
      layerDragPreview,
      keyframeDragPreview,
    } = timelineStore;
    const { applyAnimationJsonEdit } = useAnimationJsonEdit();

    const { parentLayer } = node;
    const [editorState, setEditorState] = useState<KeyframeEditorState | null>(
      null
    );
    const [hoveredSegmentId, setHoveredSegmentId] = useState<string | null>(
      null
    );
    const [
      segmentDragState,
      setSegmentDragState,
    ] = useState<SegmentDragState | null>(null);
    const [
      easingEditorState,
      setEasingEditorState,
    ] = useState<EasingEditorState | null>(null);
    const [
      addKeyframeState,
      setAddKeyframeState,
    ] = useState<AddKeyframeEditorState | null>(null);
    const segmentDragStateRef = useRef<SegmentDragState | null>(null);
    segmentDragStateRef.current = segmentDragState;
    const isSegmentDragging = segmentDragState !== null;
    const modsRevision = modsStore.trigger;

    const resolvedTiming = getResolvedLayerTiming(
      parentLayer,
      modsStore.mods?.slice() ?? []
    );
    // Check if this layer's parent is being dragged and get the delta
    const dragDelta = useMemo(
      () => getPreviewDragDeltaForLayer(layerDragPreview, parentLayer),
      [layerDragPreview, parentLayer]
    );

    const resolvedLayerData = useMemo(() => {
      void modsRevision;
      const layerData = getLayerData(jsonFile, parentLayer.layerPath);
      return applyCustomModsToLayerData(
        layerData,
        parentLayer,
        modsStore.mods?.slice() ?? []
      );
    }, [jsonFile, modsRevision, modsStore.mods, parentLayer]);

    const resolvedAnimation = jsonFile;

    // Get keyframe info with full paths from the resolved layer data
    const keyframeInfoList = useMemo(() => {
      if (!resolvedLayerData) return [];
      return getLayerKeyframeInfo(resolvedLayerData);
    }, [resolvedLayerData]);

    const propertyKeyframes = useMemo(() => {
      return keyframeInfoList.filter(
        (keyframe) => keyframe.rowId === node.rowId
      );
    }, [keyframeInfoList, node.rowId]);

    // Pre-build a Map from normalized path -> mod for O(1) lookups instead of O(n) .find() per keyframe
    const modsByNormalizedPath = useMemo(() => {
      void modsRevision;
      const map = new Map<string, IModModel>();
      const mods = modsStore.mods?.slice() ?? [];
      for (const m of mods) {
        if (!isReadableMod(m)) continue;

        const { moddata } = m;
        if (moddata.type !== modTypes.custom) continue;
        if (moddata.isDisabled) continue;
        if (typeof moddata.path !== "string") continue;
        if (!modTargetsLayer(m, parentLayer)) continue;
        const normalized = normalizeKeyframePath(moddata.path);
        // Keep the first readable match, matching the previous .find() lookup.
        if (!map.has(normalized)) {
          map.set(normalized, m);
        }
      }
      return map;
    }, [modsRevision, modsStore.mods, parentLayer]);

    // Resolve keyframe times by checking for Custom Change mods
    const resolvedKeyframes = useMemo(() => {
      return propertyKeyframes.map((kf) => {
        const normalizedPath = normalizeKeyframePath(kf.path);
        const mod = modsByNormalizedPath.get(normalizedPath);

        if (
          mod &&
          isReadableMod(mod) &&
          typeof mod.moddata.value === "number"
        ) {
          return { ...kf, t: mod.moddata.value };
        }
        return kf;
      });
    }, [propertyKeyframes, modsByNormalizedPath]);

    // Get keyframe drag functionality
    const {
      isDragging: isKeyframeDragging,
      draggedKeyframeId,
      justDragged: keyframeJustDragged,
      startDrag: startKeyframeDrag,
    } = useKeyframeDrag(compositionStart, selectedKeyframeIds);

    useEffect(() => {
      if (isKeyframeDragging) {
        setEditorState(null);
      }
    }, [isKeyframeDragging]);

    const resolvedKeyframeValuesByPath = useMemo(() => {
      const map = new Map<string, unknown>();
      propertyKeyframes.forEach((keyframe) => {
        const valueMod = modsByNormalizedPath.get(
          getKeyframeValuePath(keyframe.path)
        );
        const resolvedValue =
          valueMod && isReadableMod(valueMod)
            ? valueMod.moddata.value
            : keyframe.originalKeyframe.s;
        map.set(normalizeKeyframePath(keyframe.path), resolvedValue);
      });
      return map;
    }, [propertyKeyframes, modsByNormalizedPath]);

    const resolvedKeyframeOutgoingValuesByPath = useMemo(() => {
      const map = new Map<string, unknown>();
      propertyKeyframes.forEach((keyframe) => {
        const outgoingValueMod = modsByNormalizedPath.get(
          getKeyframeOutgoingValuePath(keyframe.path)
        );
        const resolvedOutgoingValue =
          outgoingValueMod && isReadableMod(outgoingValueMod)
            ? outgoingValueMod.moddata.value
            : keyframe.originalKeyframe.e;

        if (resolvedOutgoingValue !== undefined) {
          map.set(normalizeKeyframePath(keyframe.path), resolvedOutgoingValue);
        }
      });
      return map;
    }, [propertyKeyframes, modsByNormalizedPath]);

    const keyframeGroupsByPropertyPath = useMemo(() => {
      const map = new Map<string, KeyframeInfo[]>();
      resolvedKeyframes.forEach((keyframe) => {
        const propertyPath = getKeyframePropertyPath(keyframe.path);
        const currentGroup = map.get(propertyPath) ?? [];
        currentGroup.push(keyframe);
        map.set(propertyPath, currentGroup);
      });

      map.forEach((group, propertyPath) => {
        map.set(
          propertyPath,
          [...group].sort(
            (left, right) => left.keyframeIndex - right.keyframeIndex
          )
        );
      });

      return map;
    }, [resolvedKeyframes]);

    const applyKeyframeValue = useCallback(
      (keyframe: KeyframeInfo, draftValues: EditableKeyframeDraft) => {
        if (!draftValues.length) return;

        if (
          draftValues.some(
            (entry) => typeof entry !== "number" || !Number.isFinite(entry)
          )
        ) {
          return;
        }

        const nextValue = draftValues as number[];
        applyAnimationJsonEdit((workingAnimation) => {
          const applied = motionAPI.apply.updatePropertyKeyframe(
            workingAnimation as any,
            getTimelineMotionTarget(parentLayer),
            getKeyframePropertyPath(keyframe.path),
            { index: keyframe.keyframeIndex },
            {
              value: nextValue.length === 1 ? nextValue[0] : nextValue,
            }
          );

          return applied.changes.length ? applied : false;
        });

        setEditorState(null);
      },
      [applyAnimationJsonEdit, parentLayer]
    );

    const applyStructuredKeyframeValue = useCallback(
      (keyframe: KeyframeInfo, nextValue: unknown) => {
        const propertyPath = getKeyframePropertyPath(keyframe.path);
        const propertyGroup =
          keyframeGroupsByPropertyPath.get(propertyPath) ?? [];
        const previousKeyframe = propertyGroup.find(
          (candidate) => candidate.keyframeIndex === keyframe.keyframeIndex - 1
        );

        applyAnimationJsonEdit((workingAnimation) => {
          const workingLayerData = getLayerData(
            workingAnimation,
            parentLayer.layerPath
          );
          updateObjectValues(
            workingLayerData as Record<string, unknown>,
            [getKeyframeValuePath(keyframe.path)],
            [cloneStructuredValue(nextValue) as any]
          );

          if (previousKeyframe && previousKeyframe.originalKeyframe.h !== 1) {
            updateObjectValues(
              workingLayerData as Record<string, unknown>,
              [getKeyframeOutgoingValuePath(previousKeyframe.path)],
              [cloneStructuredValue(nextValue) as any]
            );
          }

          return true;
        });

        setEditorState(null);
      },
      [
        applyAnimationJsonEdit,
        keyframeGroupsByPropertyPath,
        parentLayer.layerPath,
      ]
    );

    const openValueEditor = useCallback(
      (keyframe: KeyframeInfo, value: unknown) => {
        const mode = getKeyframeEditorMode(keyframe, value);
        const editableValue = normalizeEditableKeyframeValue(value);

        setEditorState({
          path: normalizeKeyframePath(keyframe.path),
          mode,
          ...(editableValue
            ? {
                draftValues: editableValue,
              }
            : {
                draftText: JSON.stringify(value, null, 2),
              }),
          linkScaleXY:
            editableValue != null
              ? isScaleXYLinkable(keyframe, editableValue.length)
              : false,
        });
      },
      []
    );

    const deleteKeyframe = useCallback(
      (keyframe: KeyframeInfo, frame: number) => {
        const mode = getKeyframeEditorMode(
          keyframe,
          resolvedKeyframeValuesByPath.get(normalizeKeyframePath(keyframe.path))
        );

        if (mode === "json") {
          applyAnimationJsonEdit((workingAnimation) => {
            const workingLayerData = getLayerData(
              workingAnimation,
              parentLayer.layerPath
            );
            const propertyPath = getKeyframePropertyPath(keyframe.path);
            const currentValue = getValueAtObjectPath(
              workingLayerData,
              propertyPath
            );
            const nextValue = removeKeyframeFromAnimatedPropertyValue(
              currentValue,
              frame
            );
            if (!nextValue.removed) {
              return false;
            }

            updateObjectValues(
              workingLayerData as Record<string, unknown>,
              [propertyPath],
              [nextValue.value as any]
            );

            return true;
          });
        } else {
          applyAnimationJsonEdit((workingAnimation) => {
            const applied = motionAPI.apply.removePropertyKeyframe(
              workingAnimation as any,
              getTimelineMotionTarget(parentLayer),
              getKeyframePropertyPath(keyframe.path),
              frame
            );

            return applied.changes.length ? applied : false;
          });
        }

        setEditorState(null);
      },
      [applyAnimationJsonEdit, parentLayer, resolvedKeyframeValuesByPath]
    );

    const sortedPropertyPaths = useMemo(
      () => Array.from(keyframeGroupsByPropertyPath.keys()).sort(),
      [keyframeGroupsByPropertyPath]
    );

    const currentCompositionFrame = Math.round(currentFrame);
    const keyframeTimelineOffset = parentLayer.timelineOffset ?? 0;
    const currentLocalFrame =
      currentCompositionFrame - keyframeTimelineOffset - dragDelta;

    const keyframeConstraintById = useMemo(() => {
      const map = new Map<string, { minTime: number; maxTime: number }>();

      resolvedKeyframes.forEach((keyframe) => {
        map.set(
          buildTimelineKeyframeId(parentLayer, keyframe),
          findKeyframeTimeConstraints(keyframe, resolvedKeyframes)
        );
      });

      return map;
    }, [parentLayer, resolvedKeyframes]);

    // Calculate resolved times for all keyframes, including previews
    const keyframePositions = useMemo(() => {
      return propertyKeyframes.map((kf) => {
        const keyframeId = buildTimelineKeyframeId(parentLayer, kf);
        const normalizedPath = normalizeKeyframePath(kf.path);
        const mod = modsByNormalizedPath.get(normalizedPath);
        const resolvedTime =
          mod && isReadableMod(mod) && typeof mod.moddata.value === "number"
            ? mod.moddata.value
            : kf.t;

        const keyframeDragDelta =
          keyframeDragPreview?.keyframeDeltas?.[keyframeId] ??
          (keyframeDragPreview?.keyframeIds.includes(keyframeId)
            ? keyframeDragPreview.delta
            : 0);
        const previewTime = resolvedTime + keyframeDragDelta;
        const compositionTime = keyframeTimelineOffset + previewTime;
        const effectiveCompTime = compositionTime + dragDelta;

        return {
          keyframeId,
          kf,
          resolvedTime: previewTime,
          compositionTime: effectiveCompTime,
          resolvedValue:
            resolvedKeyframeValuesByPath.get(normalizedPath) ??
            kf.originalKeyframe.s,
          resolvedOutgoingValue: resolvedKeyframeOutgoingValuesByPath.get(
            normalizedPath
          ),
          pixel: frameToPixel(
            effectiveCompTime - compositionStart,
            zoom,
            scrollOffset
          ),
        };
      });
    }, [
      compositionStart,
      dragDelta,
      keyframeDragPreview,
      keyframeTimelineOffset,
      modsByNormalizedPath,
      parentLayer,
      propertyKeyframes,
      resolvedKeyframeOutgoingValuesByPath,
      resolvedKeyframeValuesByPath,
      scrollOffset,
      zoom,
    ]);

    const keyframePositionByPath = useMemo(() => {
      const map = new Map<string, KeyframeTimelinePosition>();

      keyframePositions.forEach((entry) => {
        map.set(normalizeKeyframePath(entry.kf.path), {
          pixel: entry.pixel,
          resolvedTime: entry.resolvedTime,
          compositionTime: entry.compositionTime,
          resolvedValue: entry.resolvedValue,
          resolvedOutgoingValue: entry.resolvedOutgoingValue,
        });
      });

      return map;
    }, [keyframePositions]);

    const segments = useMemo<KeyframeSegment[]>(() => {
      const nextSegments: KeyframeSegment[] = [];

      keyframeGroupsByPropertyPath.forEach((group, propertyPath) => {
        for (let index = 0; index < group.length - 1; index += 1) {
          const startKeyframe = group[index];
          const endKeyframe = group[index + 1];
          const startEntry = keyframePositionByPath.get(
            normalizeKeyframePath(startKeyframe.path)
          );
          const endEntry = keyframePositionByPath.get(
            normalizeKeyframePath(endKeyframe.path)
          );

          if (!startEntry || !endEntry) {
            continue;
          }

          if (
            !doesKeyframeSegmentChangeValue({
              startValue: startEntry.resolvedValue,
              startOutgoingValue: startEntry.resolvedOutgoingValue,
              endValue: endEntry.resolvedValue,
            })
          ) {
            continue;
          }

          nextSegments.push({
            id: `${normalizeKeyframePath(
              startKeyframe.path
            )}-${normalizeKeyframePath(endKeyframe.path)}`,
            startKeyframeId: buildTimelineKeyframeId(
              parentLayer,
              startKeyframe
            ),
            endKeyframeId: buildTimelineKeyframeId(parentLayer, endKeyframe),
            propertyPath,
            startKeyframe,
            endKeyframe,
            startTime: startEntry.resolvedTime,
            endTime: endEntry.resolvedTime,
            startPixel: startEntry.pixel,
            endPixel: endEntry.pixel,
            displayName: getTimelineEasingDisplayName(
              toTimelineEasingKeyframe(startKeyframe.originalKeyframe)
            ),
            easingPreset: detectTimelineEasingPreset(
              toTimelineEasingKeyframe(startKeyframe.originalKeyframe)
            ),
            bezier: getTimelineBezierValue(
              toTimelineEasingKeyframe(startKeyframe.originalKeyframe)
            ),
          });
        }
      });

      return nextSegments;
    }, [keyframeGroupsByPropertyPath, keyframePositionByPath, parentLayer]);

    useEffect(() => {
      if (!isSegmentDragging) {
        return;
      }

      const handleMouseMove = (event: MouseEvent) => {
        const currentState = segmentDragStateRef.current;
        if (!currentState) return;

        const deltaFrames = Math.round(
          (event.clientX - currentState.startClientX) / zoom
        );
        const clampedDelta = clampKeyframeDragDelta(
          deltaFrames,
          currentState.minDelta,
          currentState.maxDelta
        );
        const movedEnough =
          currentState.hasMoved ||
          Math.abs(event.clientX - currentState.startClientX) >=
            KEYFRAME_DRAG_THRESHOLD_PX;

        setSegmentDragState((previous) =>
          previous
            ? {
                ...previous,
                previewDelta: clampedDelta,
                hasMoved: movedEnough,
              }
            : previous
        );
        timelineStore.setKeyframeDragPreview({
          keyframeIds: currentState.keyframeIds,
          delta: clampedDelta,
          anchorId: currentState.keyframeIds[0] ?? "",
        });
      };

      const handleMouseUp = () => {
        const currentState = segmentDragStateRef.current;
        if (!currentState) return;

        if (currentState.hasMoved && currentState.previewDelta !== 0) {
          applyAnimationJsonEdit(
            (workingAnimation) => {
              let hasChanges = false;

              orderKeyframeDragItemsForApply(
                currentState.items,
                currentState.previewDelta
              ).forEach((item) => {
                const applied = motionAPI.apply.updatePropertyKeyframe(
                  workingAnimation as any,
                  {
                    assetId: item.assetId,
                    layerName: item.layerName,
                    layerUid: item.layerUid,
                  },
                  item.propertyPath,
                  { index: item.keyframeIndex },
                  { time: item.initialTime + currentState.previewDelta }
                );
                if (applied.changes.length) {
                  hasChanges = true;
                }
              });

              return hasChanges ? true : false;
            },
            { manageUndo: false }
          );
        } else {
          setEasingEditorState({
            segment: currentState.segment,
            preset: currentState.segment.easingPreset,
            bezier: currentState.segment.bezier,
          });
        }

        undoStore.onGestureEnd();
        setSegmentDragState(null);
        timelineStore.clearKeyframeDragPreview();
        document.body.classList.remove("timeline-keyframe-dragging");
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        timelineStore.clearKeyframeDragPreview();
        document.body.classList.remove("timeline-keyframe-dragging");
      };
    }, [
      applyAnimationJsonEdit,
      isSegmentDragging,
      timelineStore,
      undoStore,
      zoom,
    ]);

    const startSegmentInteraction = useCallback(
      (event: React.MouseEvent, segment: KeyframeSegment) => {
        event.preventDefault();
        event.stopPropagation();
        setEditorState(null);
        setAddKeyframeState(null);

        const tracksContent = (event.currentTarget as HTMLElement).closest(
          ".timeline-tracks__content"
        );
        if (!tracksContent) return;

        const markerItems = getMarkerElements(tracksContent)
          .map((element) => parseMarkerDragItem(element))
          .filter((item): item is CapturedKeyframeDragItem => item !== null);
        const segmentIds = new Set([
          segment.startKeyframeId,
          segment.endKeyframeId,
        ]);
        const shouldMoveSelection =
          selectedKeyframeIds.has(segment.startKeyframeId) ||
          selectedKeyframeIds.has(segment.endKeyframeId);
        const targetIds = shouldMoveSelection
          ? new Set([
              ...Array.from(selectedKeyframeIds),
              segment.startKeyframeId,
              segment.endKeyframeId,
            ])
          : segmentIds;
        const selectedItems = markerItems.filter((item) =>
          targetIds.has(item.id)
        );
        const selectedIds = new Set(selectedItems.map((item) => item.id));
        const { minDelta, maxDelta } = getSelectedKeyframeDragBounds(
          markerItems.map((item) => ({
            id: item.id,
            groupKey: getKeyframeDragGroupKey(item),
            keyframeIndex: item.keyframeIndex,
            initialTime: item.initialTime,
          })),
          selectedIds
        );

        if (!selectedItems.length) return;

        undoStore.onGestureStart();
        timelineStore.setKeyframeDragPreview({
          keyframeIds: selectedItems.map((item) => item.id),
          delta: 0,
          anchorId: segment.startKeyframeId,
        });
        document.body.classList.add("timeline-keyframe-dragging");
        setSegmentDragState({
          segment,
          items: selectedItems,
          keyframeIds: selectedItems.map((item) => item.id),
          startClientX: event.clientX,
          previewDelta: 0,
          minDelta,
          maxDelta,
          hasMoved: false,
        });
      },
      [selectedKeyframeIds, timelineStore, undoStore]
    );

    const applySegmentEasing = useCallback(() => {
      if (!easingEditorState) return;

      undoStore.onGestureStart();
      applyAnimationJsonEdit(
        (workingAnimation) => {
          const applied = motionAPI.apply.updatePropertyKeyframe(
            workingAnimation as any,
            getTimelineMotionTarget(parentLayer),
            getKeyframePropertyPath(
              easingEditorState.segment.startKeyframe.path
            ),
            { index: easingEditorState.segment.startKeyframe.keyframeIndex },
            getMotionEasingPatch(
              easingEditorState.preset,
              easingEditorState.bezier
            )
          );

          return applied.changes.length ? applied : false;
        },
        { manageUndo: false }
      );
      undoStore.onGestureEnd();
      setEasingEditorState(null);
    }, [applyAnimationJsonEdit, easingEditorState, parentLayer, undoStore]);

    const addKeyframeCandidate = useMemo(() => {
      const effectiveLayerStart = resolvedTiming.ip + dragDelta;
      const effectiveLayerEnd = resolvedTiming.op + dragDelta;
      if (
        currentFrame < effectiveLayerStart ||
        currentFrame > effectiveLayerEnd
      ) {
        return null;
      }

      if (!sortedPropertyPaths.length) {
        return null;
      }

      const motionProperty = getMotionPropertyForRow(
        node.propertyType,
        sortedPropertyPaths
      );
      if (!motionProperty && sortedPropertyPaths.length !== 1) {
        return null;
      }

      const fields: AddKeyframeField[] = [];
      const draftValues: EditableKeyframeDraft = [];
      let hasExactKeyframeAtCurrentFrame = false;
      let linkScaleXY = false;
      let mode: AddKeyframeEditorState["mode"] = "numeric";

      sortedPropertyPaths.forEach((propertyPath) => {
        const group = keyframeGroupsByPropertyPath.get(propertyPath) ?? [];
        if (!group.length) return;

        const {
          hasExactKeyframeAtFrame,
          labelSourceKeyframe,
          sourceValue,
        } = getAddKeyframeDraftSource({
          group,
          keyframePositionByPath,
          currentLocalFrame,
          modsByNormalizedPath,
        });

        if (hasExactKeyframeAtFrame) {
          hasExactKeyframeAtCurrentFrame = true;
          return;
        }

        if (!sourceValue || !labelSourceKeyframe) return;

        const fieldDescriptors =
          sortedPropertyPaths.length > 1
            ? Array.from({ length: sourceValue.length }, (_, index) => ({
                label:
                  sourceValue.length === 1
                    ? labelSourceKeyframe.propertyName
                    : `${labelSourceKeyframe.propertyName} ${index + 1}`,
                componentIndex: index,
              }))
            : getKeyframeFieldDescriptors(
                labelSourceKeyframe,
                sourceValue.length
              );

        if (sortedPropertyPaths.length === 1) {
          linkScaleXY = isScaleXYLinkable(
            labelSourceKeyframe,
            sourceValue.length
          );
          mode = isColorKeyframeValue(labelSourceKeyframe, sourceValue)
            ? "color"
            : "numeric";
        }

        sourceValue.forEach((value) => {
          draftValues.push(value);
        });

        fieldDescriptors.forEach(({ label, componentIndex }) => {
          fields.push({
            id: `${propertyPath}-${componentIndex}`,
            label,
            path: propertyPath,
            componentIndex,
          });
        });
      });

      if (hasExactKeyframeAtCurrentFrame) {
        return null;
      }

      return fields.length
        ? {
            localFrame: currentLocalFrame,
            fields,
            draftValues,
            propertyPaths: sortedPropertyPaths,
            linkScaleXY,
            mode,
          }
        : null;
    }, [
      currentFrame,
      currentLocalFrame,
      dragDelta,
      keyframeGroupsByPropertyPath,
      keyframePositionByPath,
      modsByNormalizedPath,
      node.propertyType,
      resolvedTiming.ip,
      resolvedTiming.op,
      sortedPropertyPaths,
    ]);

    const openAddKeyframeEditor = useCallback(() => {
      if (!addKeyframeCandidate) return;
      setEditorState(null);
      setEasingEditorState(null);
      setAddKeyframeState(addKeyframeCandidate);
    }, [addKeyframeCandidate]);

    const applyAddedKeyframe = useCallback(() => {
      if (!addKeyframeState || !resolvedAnimation) return;

      const motionProperty = getMotionPropertyForRow(
        node.propertyType,
        addKeyframeState.propertyPaths
      );
      if (!motionProperty && addKeyframeState.propertyPaths.length !== 1) {
        return;
      }

      const nextValue = normalizeDraftValues(addKeyframeState.draftValues);
      if (!nextValue) {
        return;
      }

      const timing = {
        startFrame: addKeyframeState.localFrame,
        endFrame: addKeyframeState.localFrame,
      };

      applyAnimationJsonEdit((workingAnimation) => {
        const applied = motionAPI.apply.applyPropertyAnimation(
          workingAnimation as any,
          getTimelineMotionTarget(parentLayer),
          {
            property: motionProperty ?? addKeyframeState.propertyPaths[0],
            timing,
            toValue: nextValue.length === 1 ? nextValue[0] : nextValue,
            conflictPolicy: "replaceInRange",
            phase: "out",
            baseValueSource: "valueAtStart",
          }
        );

        if (!applied.changes.length) {
          return false;
        }

        setAddKeyframeState(null);
        return applied;
      });
    }, [
      addKeyframeState,
      applyAnimationJsonEdit,
      node.propertyType,
      parentLayer,
      resolvedAnimation,
    ]);

    const playheadAddButtonPixel = addKeyframeCandidate
      ? frameToPixel(currentFrame - compositionStart, zoom, scrollOffset)
      : null;

    return (
      <div
        className="timeline-tree-track timeline-tree-track--property"
        style={{ height: TREE_ROW_HEIGHT }}
      >
        {segments.map((segment) => (
          <Popover
            key={segment.id}
            open={easingEditorState?.segment.id === segment.id}
            trigger={[]}
            placement="top"
            content={
              easingEditorState?.segment.id === segment.id ? (
                <div
                  className="timeline-segment-easing-popover"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                >
                  <KeyframeEasingEditor
                    preset={easingEditorState.preset}
                    bezier={easingEditorState.bezier}
                    onPresetChange={(preset) => {
                      setEasingEditorState((previous) =>
                        previous
                          ? {
                              ...previous,
                              preset,
                              bezier:
                                preset === "custom" || preset === "hold"
                                  ? previous.bezier
                                  : TIMELINE_EASING_PRESETS[preset],
                            }
                          : previous
                      );
                    }}
                    onBezierChange={(bezier) => {
                      setEasingEditorState((previous) =>
                        previous
                          ? {
                              ...previous,
                              preset: "custom",
                              bezier,
                            }
                          : previous
                      );
                    }}
                  />
                  <div className="timeline-keyframe-editor__actions">
                    <Button
                      size="small"
                      onClick={() => setEasingEditorState(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      onClick={applySegmentEasing}
                    >
                      OK
                    </Button>
                  </div>
                </div>
              ) : null
            }
          >
            <div
              className={`timeline-keyframe-row__segment ${
                hoveredSegmentId === segment.id
                  ? "timeline-keyframe-row__segment--hovered"
                  : ""
              } ${
                segmentDragState?.segment.id === segment.id
                  ? "timeline-keyframe-row__segment--dragging"
                  : ""
              }`}
              style={{
                left: segment.startPixel,
                width: Math.max(0, segment.endPixel - segment.startPixel),
              }}
              onMouseEnter={() => setHoveredSegmentId(segment.id)}
              onMouseLeave={() =>
                setHoveredSegmentId((previous) =>
                  previous === segment.id ? null : previous
                )
              }
              onMouseDown={(event) => startSegmentInteraction(event, segment)}
            >
              <span className="timeline-keyframe-row__segment-label">
                {segment.displayName}
              </span>
            </div>
          </Popover>
        ))}

        {playheadAddButtonPixel !== null && addKeyframeCandidate && (
          <Popover
            open={addKeyframeState !== null}
            trigger="click"
            placement="rightTop"
            onOpenChange={(open) => {
              if (!open) {
                setAddKeyframeState(null);
              }
            }}
            content={
              addKeyframeState ? (
                <div
                  className="timeline-keyframe-editor"
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <div className="timeline-keyframe-editor__header">
                    <div className="timeline-keyframe-editor__title">
                      Add {node.propertyName} Keyframe
                    </div>
                    {node.propertyType === "scale" &&
                    addKeyframeState.fields.length > 1 ? (
                      <ScaleProportionToggle
                        checked={addKeyframeState.linkScaleXY}
                        onChange={(nextState) => {
                          setAddKeyframeState((previous) => {
                            if (!previous) return previous;
                            return {
                              ...previous,
                              linkScaleXY: nextState,
                            };
                          });
                        }}
                      />
                    ) : null}
                  </div>
                  {addKeyframeState.mode === "color" ? (
                    <div className="timeline-keyframe-editor__color-field">
                      <span>Color</span>
                      <ColorPicker
                        color={toColorHex(
                          addKeyframeState.draftValues as number[]
                        )}
                        onChange={(nextColor) => {
                          setAddKeyframeState((previous) =>
                            previous
                              ? {
                                  ...previous,
                                  draftValues: fromColorHex(
                                    nextColor.hex,
                                    previous.draftValues
                                  ),
                                }
                              : previous
                          );
                        }}
                        size={2}
                      />
                    </div>
                  ) : (
                    addKeyframeState.fields.map((field) => (
                      <label
                        key={field.id}
                        className="timeline-keyframe-editor__field"
                      >
                        <span>{field.label}</span>
                        <InputNumber
                          size="small"
                          value={
                            addKeyframeState.draftValues[
                              field.componentIndex
                            ] ?? null
                          }
                          onChange={(nextValue) => {
                            setAddKeyframeState((previous) => {
                              if (!previous) return previous;
                              const nextDraftValues = previous.draftValues.slice();
                              nextDraftValues[field.componentIndex] = nextValue;
                              return {
                                ...previous,
                                draftValues:
                                  previous.linkScaleXY &&
                                  field.componentIndex <= 1
                                    ? syncLinkedScaleDraftValues(
                                        nextDraftValues,
                                        field.componentIndex,
                                        previous.draftValues
                                      )
                                    : nextDraftValues,
                              };
                            });
                          }}
                        />
                      </label>
                    ))
                  )}
                  <div className="timeline-keyframe-editor__actions">
                    <Button
                      size="small"
                      onClick={() => setAddKeyframeState(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      onClick={applyAddedKeyframe}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              ) : null
            }
          >
            <button
              type="button"
              className={`timeline-keyframe-row__add-button ${
                addKeyframeState
                  ? "timeline-keyframe-row__add-button--visible"
                  : ""
              }`}
              style={{ left: playheadAddButtonPixel }}
              onClick={(event) => {
                event.stopPropagation();
                openAddKeyframeEditor();
              }}
              aria-label="Add keyframe"
            >
              <HoverTooltip
                title="Add Keyframe"
                description="Add a keyframe at the current playhead position."
                placement="top"
              >
                <span className="timeline-keyframe-row__tooltip-icon">
                  <PlusOutlined />
                </span>
              </HoverTooltip>
            </button>
          </Popover>
        )}

        {/* Keyframe markers */}
        {keyframePositions.map(
          ({
            keyframeId,
            kf,
            pixel,
            resolvedValue,
            resolvedTime,
            compositionTime,
          }) => {
            const MIN_LEFT_OFFSET = 5;
            const markerLeft = Math.max(pixel, MIN_LEFT_OFFSET);
            const isBeingDragged =
              isKeyframeDragging &&
              keyframeDragPreview?.anchorId === keyframeId &&
              draggedKeyframeId === keyframeId;
            const isSelected = selectedKeyframeIds.has(keyframeId);
            const normalizedKeyframePath = normalizeKeyframePath(kf.path);
            const isEditorOpen = editorState?.path === normalizedKeyframePath;
            const editableValue = normalizeEditableKeyframeValue(resolvedValue);
            const editorMode = getKeyframeEditorMode(kf, resolvedValue);
            const fieldDescriptors = editableValue
              ? getKeyframeFieldDescriptors(kf, editableValue.length)
              : [];
            const showScaleLinkToggle =
              editableValue !== null &&
              isScaleXYLinkable(kf, editableValue.length);
            const constraints = keyframeConstraintById.get(keyframeId) ?? {
              minTime: 0,
              maxTime: Infinity,
            };
            const markerDataAttributes = buildTimelineKeyframeDataAttributes({
              id: keyframeId,
              layer: parentLayer,
              keyframe: kf,
              resolvedTime,
              compositionTime,
              minTime: constraints.minTime,
              maxTime: constraints.maxTime,
            });

            return (
              <Popover
                key={`kf-${kf.path}`}
                open={isEditorOpen}
                trigger="click"
                placement="rightTop"
                onOpenChange={(open) => {
                  if (!open && isEditorOpen) {
                    setEditorState(null);
                  }
                }}
                content={
                  isEditorOpen ? (
                    <div
                      className="timeline-keyframe-editor"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="timeline-keyframe-editor__header">
                        <div className="timeline-keyframe-editor__title">
                          {kf.propertyName}
                        </div>
                        {showScaleLinkToggle ? (
                          <ScaleProportionToggle
                            checked={editorState?.linkScaleXY ?? false}
                            onChange={(nextState) => {
                              setEditorState((prev) => {
                                if (
                                  !prev ||
                                  prev.path !== normalizedKeyframePath
                                ) {
                                  return prev;
                                }
                                return {
                                  ...prev,
                                  linkScaleXY: nextState,
                                };
                              });
                            }}
                          />
                        ) : null}
                      </div>
                      {editorMode === "color" && editorState?.draftValues ? (
                        <div className="timeline-keyframe-editor__color-field">
                          <span>Color</span>
                          <ColorPicker
                            color={toColorHex(
                              editorState.draftValues as number[]
                            )}
                            onChange={(nextColor) => {
                              setEditorState((prev) =>
                                prev && prev.path === normalizedKeyframePath
                                  ? {
                                      ...prev,
                                      draftValues: fromColorHex(
                                        nextColor.hex,
                                        prev.draftValues ?? []
                                      ),
                                    }
                                  : prev
                              );
                            }}
                            size={2}
                          />
                        </div>
                      ) : editorMode === "json" ? (
                        <label className="timeline-keyframe-editor__json-field">
                          <span>JSON</span>
                          <Input.TextArea
                            value={editorState?.draftText ?? ""}
                            autoSize={{ minRows: 5, maxRows: 10 }}
                            onChange={(event) => {
                              setEditorState((prev) =>
                                prev && prev.path === normalizedKeyframePath
                                  ? {
                                      ...prev,
                                      draftText: event.target.value,
                                    }
                                  : prev
                              );
                            }}
                          />
                        </label>
                      ) : (
                        fieldDescriptors.map(({ label, componentIndex }) => (
                          <label
                            key={`${normalizedKeyframePath}-${label}`}
                            className="timeline-keyframe-editor__field"
                          >
                            <span>{label}</span>
                            <InputNumber
                              size="small"
                              value={
                                editorState?.draftValues?.[componentIndex] ??
                                null
                              }
                              onChange={(nextValue) => {
                                setEditorState((prev) => {
                                  if (
                                    !prev ||
                                    prev.path !== normalizedKeyframePath
                                  ) {
                                    return prev;
                                  }
                                  const nextDraftValues = (
                                    prev.draftValues ?? []
                                  ).slice();
                                  nextDraftValues[componentIndex] = nextValue;
                                  return {
                                    ...prev,
                                    draftValues:
                                      prev.linkScaleXY && componentIndex <= 1
                                        ? syncLinkedScaleDraftValues(
                                            nextDraftValues,
                                            componentIndex,
                                            prev.draftValues ?? []
                                          )
                                        : nextDraftValues,
                                  };
                                });
                              }}
                            />
                          </label>
                        ))
                      )}
                      <div className="timeline-keyframe-editor__actions">
                        <Button
                          size="small"
                          onClick={() => deleteKeyframe(kf, resolvedTime)}
                        >
                          Delete
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setEditorState(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => {
                            if (editorMode === "json") {
                              if (!editorState?.draftText) {
                                return;
                              }

                              try {
                                applyStructuredKeyframeValue(
                                  kf,
                                  JSON.parse(editorState.draftText)
                                );
                              } catch {
                                return;
                              }
                              return;
                            }

                            applyKeyframeValue(
                              kf,
                              editorState?.draftValues ?? []
                            );
                          }}
                        >
                          OK
                        </Button>
                      </div>
                    </div>
                  ) : null
                }
              >
                <Tooltip
                  placement="top"
                  open={isEditorOpen ? false : undefined}
                  title={
                    <div className="timeline-keyframe-tooltip">
                      <div className="timeline-keyframe-tooltip__title">
                        {kf.propertyName}
                      </div>
                      <div>{formatKeyframeValueLabel(kf, resolvedValue)}</div>
                    </div>
                  }
                >
                  <div
                    className={`timeline-keyframe-marker ${
                      isSelected ? "timeline-keyframe-marker--selected" : ""
                    } ${
                      isBeingDragged ? "timeline-keyframe-marker--dragging" : ""
                    }`}
                    style={{ left: markerLeft }}
                    {...markerDataAttributes}
                    onMouseDown={(event) => {
                      if (event.shiftKey || event.metaKey || event.ctrlKey) {
                        event.stopPropagation();
                        return;
                      }

                      startKeyframeDrag(event);
                    }}
                    onClick={(event) => {
                      if (keyframeJustDragged) return;
                      onKeyframeClick(keyframeId, event);
                      if (event.shiftKey || event.metaKey || event.ctrlKey) {
                        event.preventDefault();
                        event.stopPropagation();
                        return;
                      }

                      openValueEditor(kf, resolvedValue);
                    }}
                  />
                </Tooltip>
              </Popover>
            );
          }
        )}
      </div>
    );
  }
);

type ScaleProportionToggleProps = {
  checked: boolean;
  onChange: (nextState: boolean) => void;
};

function ScaleProportionToggle({
  checked,
  onChange,
}: ScaleProportionToggleProps) {
  return (
    <label className="timeline-keyframe-editor__proportion-toggle">
      <input
        type="checkbox"
        className="timeline-keyframe-editor__proportion-checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        aria-label="Lock proportions"
      />
      <span
        className="timeline-keyframe-editor__proportion-switch"
        aria-hidden="true"
      />
      <span>Lock proportions</span>
    </label>
  );
}

// Main component that renders either layer or property track
export const TimelineTreeNodeTrack = observer(
  ({
    node,
    compositionStart,
    compositionEnd,
    totalFrames,
    isSelected,
    isHovered,
    isRowHovered,
    onClick,
    onContextMenu,
    onMouseEnter,
    onMouseLeave,
    selectedLayersWithTiming,
    selectedKeyframeIds,
    onKeyframeClick,
    clipToWorkarea,
  }: TimelineTreeNodeTrackProps) => {
    if (node.type === "layer") {
      return (
        <LayerTrack
          node={node}
          compositionStart={compositionStart}
          compositionEnd={compositionEnd}
          totalFrames={totalFrames}
          isSelected={isSelected}
          isHovered={isHovered}
          isRowHovered={isRowHovered}
          onClick={onClick}
          onContextMenu={onContextMenu}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          selectedLayersWithTiming={selectedLayersWithTiming}
          clipToWorkarea={clipToWorkarea}
        />
      );
    }

    // Property node
    return (
      <PropertyTrack
        node={node}
        compositionStart={compositionStart}
        selectedKeyframeIds={selectedKeyframeIds}
        onKeyframeClick={onKeyframeClick}
      />
    );
  }
);

// Keep old export for backwards compatibility during migration
export { TimelineTreeNodeTrack as TimelineTrack };
